import React from 'react';

/**
 * Farm-mérőszám kiírása.
 *
 * Egyszerű valutánál egy szám (`+1,727`). Aminek VÁLTÓPÉNZE van (arany), ott a
 * teljes felosztás: `14,566g 87s 43c`.
 *
 * A felosztás a mérőszám-leíró `units` mezőjéből jön (a backend `farmMetrics.js`-e
 * küldi), tehát új valuta felvétele itt sem igényel módosítást.
 *
 * ── Miért így, és nem osztással ──────────────────────────────────────────────
 * A `divisor`-ral osztott szám LEFELÉ KEREKÍT, tehát a `14,566g` valójában lehet
 * 14 566 arany 87 ezüst 43 réz. Az adatbázis a nyers rezet tárolja, a váltópénz
 * tehát megvan — csak eddig nem látszott.
 *
 * A számolás és a nulla tagok elhagyása a JÁTÉK saját logikáját követi
 * (`Util::SharedMoneyDisplay::CreateVCDisplayElement`, `sharedmoneydisplay.cc`):
 * ott is `nyers / 10000` az arany, `(nyers % 10000) / 100` az ezüst, a maradék a
 * réz, és a nulla tagok kimaradnak (ezért van a játékban külön `TemplateGoldSilver`,
 * `TemplateGoldCopper`, `TemplateGold` sablon).
 */

/** Ezres tagolás, egész számra. */
const group = (n) => Math.floor(n).toLocaleString('en-US');

/**
 * Nyers értékből a kiírandó tagok.
 *
 * A NULLA TAGOK KIMARADNAK (`14,566g 43c`, nem `14,566g 0s 43c`), kivéve ha
 * minden nulla — akkor a legnagyobb egység `0`-ja marad, hogy legyen mit mutatni.
 */
export function amountParts(raw, metric) {
  let rest = Math.max(0, Math.floor(Number(raw) || 0));

  if (!metric?.units?.length) {
    return [`${group(rest / (metric?.divisor || 1))}${metric?.suffix || ''}`];
  }

  const parts = [];
  for (const u of metric.units) {
    const n = Math.floor(rest / u.factor);
    rest -= n * u.factor;
    if (n > 0) parts.push(`${group(n)}${u.suffix}`);
  }

  if (parts.length === 0) {
    const biggest = metric.units[0];
    return [`0${biggest.suffix}`];
  }
  return parts;
}

/**
 * A legnagyobb egység hangsúlyos, a váltópénz halványabb és kisebb — így egy
 * pillantással a lényeg (az arany) olvasható, de a pontos érték is ott van.
 *
 * A halványítás `opacity`-vel megy, nem külön színnel: így a szülő színét
 * (zöld bevétel / piros költés) örökli, és nem kell színt átadni.
 */
export default function Amount({ value, metric, sign = '', className = '', tailClassName = 'text-[0.8em]' }) {
  const [head, ...tail] = amountParts(value, metric);

  return (
    <span className={className}>
      {sign}{head}
      {tail.length > 0 && (
        <span className={`ml-1 opacity-65 font-normal ${tailClassName}`}>{tail.join(' ')}</span>
      )}
    </span>
  );
}
